import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaChevronDown, FaSearch } from "react-icons/fa";
import { usePortalPosition } from "../../TaskManagement/usePortalPosition";
import {
  FILTER_CHEVRON_CLASS,
  FILTER_DROPDOWN_LI_RESET,
  FILTER_DROPDOWN_LIST_STYLE,
  FILTER_LABEL_CLASS,
  FILTER_TRIGGER_CLASS,
  FILTER_TRIGGER_OPEN_CLASS,
  getDropdownOptionStyle,
} from "./commissionsConstants";

function CommissionsSelect({
  label,
  value = null,
  onChange,
  options = [],
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  disabled = false,
  usePortal = true,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const portalStyle = usePortalPosition(triggerRef, open && usePortal, {
    placement: "bottom",
    gap: 6,
  });

  const normalizedOptions = useMemo(
    () =>
      options.map((opt) => ({
        value: opt.id ?? opt.value,
        label: opt.name ?? opt.title ?? opt.label ?? String(opt.id),
        data: opt,
      })),
    [options],
  );

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return normalizedOptions;
    return normalizedOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [normalizedOptions, search]);

  const selectedOption = useMemo(
    () => normalizedOptions.find((o) => String(o.value) === String(value)) || null,
    [normalizedOptions, value],
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inContainer = containerRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inContainer && !inMenu) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 0);
    else setSearch("");
  }, [open]);

  const selectOption = (option) => {
    if (String(option.value) === String(value)) {
      onChange(null);
    } else {
      onChange(option.value);
    }
    setOpen(false);
    setSearch("");
  };

  const menu = (
    <div
      ref={menuRef}
      className={`tailwind-container overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg ${
        usePortal ? "z-[9999]" : "absolute z-[60] mt-1 w-full"
      }`}
      style={
        usePortal
          ? portalStyle
          : undefined
      }
    >
      <div className="border-b border-gray-100 bg-[#FAFBFC] p-2">
        <div className="relative">
          <FaSearch
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
            size={11}
          />
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-[34px] w-full rounded-lg border border-gray-200 bg-white pl-8 pr-2 text-[13px] text-gray-900 outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
      <ul className="max-h-48 overflow-x-hidden overflow-y-auto py-1" style={FILTER_DROPDOWN_LIST_STYLE}>
        {filteredOptions.length === 0 ? (
          <li className="px-3 py-2 text-center text-[13px] text-gray-400" style={FILTER_DROPDOWN_LI_RESET}>
            No results found
          </li>
        ) : (
          filteredOptions.map((option) => {
            const selected = String(option.value) === String(value);
            return (
              <li key={option.value} style={FILTER_DROPDOWN_LI_RESET}>
                <button
                  type="button"
                  onClick={() => selectOption(option)}
                  className="flex w-full min-w-0 items-center overflow-hidden text-[13px] transition"
                  style={getDropdownOptionStyle(selected)}
                  onMouseEnter={(e) => {
                    if (!selected) e.currentTarget.style.backgroundColor = "#F9FAFB";
                  }}
                  onMouseLeave={(e) => {
                    if (!selected) e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <span className="min-w-0 truncate">{option.label}</span>
                </button>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );

  return (
    <div className="min-w-0 flex-1 basis-0" ref={containerRef}>
      {label && <label className={FILTER_LABEL_CLASS}>{label}</label>}
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen((p) => !p)}
          className={`${FILTER_TRIGGER_CLASS} ${
            open ? FILTER_TRIGGER_OPEN_CLASS : ""
          } ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
        >
          <span className={`truncate ${selectedOption ? "text-gray-800" : "text-gray-400"}`}>
            {selectedOption?.label || placeholder}
          </span>
          <FaChevronDown
            size={10}
            className={`${FILTER_CHEVRON_CLASS} ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && !disabled && (usePortal ? createPortal(menu, document.body) : menu)}
      </div>
    </div>
  );
}

export default CommissionsSelect;

export function CommissionsSingleSelect(props) {
  return <CommissionsSelect {...props} />;
}
